import React from 'react';
import Main from '../layouts/Main';
import { Alert, Button, Grid, Link, Typography } from '@mui/material';
import TextField from '@mui/material/TextField';
import SendIcon from '@mui/icons-material/Send';
import CheckIcon from '@mui/icons-material/Check';
import validateEmail from '../utils/validateEmail';
import { createContactForm } from '../ajax/contact_form';
import Head from 'next/head';
import { CANONICAL_DOMAIN } from '../utils/constants';

const initialForm = {
  name: '',
  email: '',
  message: '',
};

interface Errors {
  email?: string;
  name?: string;
  message?: string;
}

export default function ContactUs() {
  const [form, setForm] = React.useState(initialForm);
  const [errors, setErrors] = React.useState<Errors>({});
  const [formError, setFormError] = React.useState('');
  const [success, setSuccess] = React.useState(false);

  React.useEffect(() => {
    if (success) {
      setTimeout(() => {
        setSuccess(false);
      }, 10000);
    }
  }, [success]);

  const handleFormChange: React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement> = (e) => {
    setForm((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const validateForm = () => {
    const errors: Errors = {};
    if (!form.email) {
      errors.email = 'Email is Required';
    } else if (!validateEmail(form.email)) {
      errors.email = 'Invalid Email Address';
    }
    if (!form.name) {
      errors.name = 'Name is Required';
    }
    if (!form.message) {
      errors.message = 'A Message is Required';
    }
    return errors;
  };

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    const _errors = validateForm();
    if (Object.keys(_errors).length == 0) {
      setErrors({});
      createContactForm(form.name, form.email, form.message).then((success) => {
        if (success) {
          setSuccess(true);
          setFormError('');
          setForm(initialForm);
        } else {
          setFormError("We're sorry. There was a problem submitting your message. Please try again, or send us an email directly.");
        }
      });
    } else {
      setErrors(_errors);
    }
  };

  return (
    <Main header_title="We'd Love to Hear from You!">
      <Head>
        <title>Fosterburg Baptist Church | Contact Us</title>
        <meta property="og:title" content="Fosterburg Baptist Church | Contact Us" key="title" />
        <link rel="canonical" href={`${CANONICAL_DOMAIN}/contact-us`} />
      </Head>
      <div>
        <Grid container>
          <Grid item xs={12} md={4}>
            <div style={{ margin: 35, marginLeft: 50 }}>
              <div style={{ marginBottom: 15 }}>
                <Typography variant="h5">Address</Typography>
                <Link
                  href="https://www.google.com/maps/place/Fosterburg+Baptist+Church/@38.968558,-90.073349,16.59z/data=!4m6!3m5!1s0x87df5de0722f7ec7:0xd598fbe7d87860ac!8m2!3d38.9692163!4d-90.0747816!16s%2Fg%2F1tcyscml"
                  target="_blank"
                  rel="noreferrer"
                  underline="hover"
                  color="black"
                >
                  <Typography>4737 Fosterburg Rd</Typography>
                  <Typography>Bethalto, IL 62010</Typography>
                </Link>
              </div>
              <div style={{ marginBottom: 15 }}>
                <Typography variant="h5">Phone</Typography>
                <Link href="tel:6182592550" underline="hover" color="black">
                  <Typography>(618) 259-2550</Typography>
                </Link>
              </div>
              <Typography variant="h5">Office Hours</Typography>
              <Typography>Tuesday - Friday</Typography>
              <Typography>9am - 3pm</Typography>
            </div>
          </Grid>
          <Grid item xs={12} md={8}>
            <div style={{ textAlign: 'center', margin: 35 }}>
              <div style={{ marginBottom: 15 }}>
                <Typography variant="h5">Contact Us</Typography>
              </div>
              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: 15 }}>
                  <TextField
                    label="Name"
                    name="name"
                    value={form.name}
                    onChange={handleFormChange}
                    required={true}
                    fullWidth={true}
                    error={!!errors.name}
                    helperText={errors.name}
                  />
                </div>
                <div style={{ marginBottom: 15 }}>
                  <TextField
                    label="Email"
                    name="email"
                    value={form.email}
                    onChange={handleFormChange}
                    required={true}
                    fullWidth={true}
                    error={!!errors.email}
                    helperText={errors.email}
                  />
                </div>
                <div style={{ marginBottom: 15 }}>
                  <TextField
                    placeholder="Type your message here"
                    name="message"
                    value={form.message}
                    onChange={handleFormChange}
                    required={true}
                    fullWidth={true}
                    multiline={true}
                    minRows={3}
                    error={!!errors.message}
                    helperText={errors.message}
                  />
                </div>
                {!!formError && (
                  <div style={{ marginBottom: 15 }}>
                    <Alert severity="error">{formError}</Alert>
                  </div>
                )}
                {!!success && (
                  <div style={{ marginBottom: 15 }}>
                    <Alert icon={<CheckIcon fontSize="inherit" />} severity="success">
                      Thanks! We have received your message and will respond to you shortly.
                    </Alert>
                  </div>
                )}
                <Button type="submit" variant="contained" fullWidth={true} endIcon={<SendIcon />}>
                  Send
                </Button>
              </form>
            </div>
          </Grid>
        </Grid>
      </div>
    </Main>
  );
}
